import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import { GetTreatmentDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';


interface Props {
  data: GetTreatmentDto[];
  onRowClick: (treatmentId: string) => void;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export default function OrganizationTreatmentsCatalogTable({
  data,
  onRowClick,
}: Props) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginated = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const TableHeader = (
    <TableHead>
      <TableRow>
        <TableCell>Nazwa</TableCell>
        <TableCell>{translateKey("search.ui.aboutTab")}</TableCell>
        <TableCell>Leki</TableCell>
        <TableCell align="center">{translateKey("visit.tableStatus")}</TableCell>
        <TableCell align="center">Cena netto</TableCell>
        <TableCell align="center">VAT</TableCell>
        <TableCell align="center">Cena brutto</TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {paginated.length > 0 ? (
          <Table stickyHeader>
            {TableHeader}
            <TableBody>
              {paginated.map((treatment) => (
                <TableRow
                  key={treatment.id}
                  hover
                  data-testid="treatment-catalog-row"
                  data-treatment-id={treatment.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onRowClick(treatment.id)}
                >
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {treatment.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {treatment.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      gap={0.5}
                    >
                      {treatment.associatedMedicines?.length > 0
                        ? treatment.associatedMedicines.map((m) => (
                            <Chip
                              key={m.medicineProductId}
                              label={m.medicineName}
                              size="small"
                              variant="outlined"
                            />
                          ))
                        : '-'}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {treatment.isActive ? (
                      <Iconify
                        icon="eva:checkmark-circle-2-fill"
                        width={20}
                        sx={{ color: 'success.main' }}
                      />
                    ) : (
                      <Iconify
                        icon="eva:close-circle-outline"
                        width={20}
                        sx={{ color: 'text.disabled' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {treatment.priceNetto != null
                      ? `${treatment.priceNetto.toFixed(2)} ${translateKey("hardcoded.currencyPln")}`
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {treatment.vatRate != null
                      ? `${treatment.vatRate}%`
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {treatment.priceBrutto != null
                      ? `${treatment.priceBrutto.toFixed(2)} ${translateKey("hardcoded.currencyPln")}`
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <>
            <Table stickyHeader>{TableHeader}</Table>
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty treatments"
                src="/assets/images/empty_table.svg"
              />
              <Typography>{translateKey("hardcoded.brak_zabiegow_w_katalogu")}</Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={translateKey("hardcoded.wierszy_na_strone_2")}
      />
    </>
  );
}
